import React, { useEffect, useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { useGetLeadDistributionStatsMutation } from '../../api/usersAPISlices';

const WeeklyDistributionRate = () => {
  const [chartData, setChartData] = useState({ weeks: [], rates: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [getLeadDistributionStats] = useGetLeadDistributionStatsMutation();
  
  const token = JSON.parse(localStorage.getItem("token"));

  // Helper function to get California time
  const getCaliforniaDate = (date = new Date()) => {
    const californiaTime = new Date(date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
    return californiaTime;
  };

  // Function to get the start of a week (Monday) in California time
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Function to format date for display
  const formatWeekLabel = (date) => {
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  useEffect(() => {
    fetchWeeklyData();
  }, []);

  const fetchWeeklyData = async () => {
    setIsLoading(true);
    
    try {
      const today = getCaliforniaDate();
      const weeks = [];
      const rates = [];
      
      // Calculate data for the last 6 weeks
      for (let i = 5; i >= 0; i--) {
        const weekEnd = new Date(today);
        weekEnd.setDate(today.getDate() - (i * 7));
        
        const weekStart = new Date(weekEnd);
        weekStart.setDate(weekEnd.getDate() - 6);
        
        // Format dates for API
        const startDate = formatDateForAPI(weekStart);
        const endDate = formatDateForAPI(weekEnd);
        
        // Fetch data for this week
        try {
          const res = await getLeadDistributionStats({ 
            token, 
            startDate, 
            endDate 
          }).unwrap();
          
          // Calculate average distribution rate for the week
          let totalRecorded = 0;
          let totalDistributed = 0;
          
          if (res.daily_distribution && Array.isArray(res.daily_distribution)) {
            res.daily_distribution.forEach(day => {
              totalRecorded += (day.recorded || 0);
              totalDistributed += (day.distributed || 0);
            });
          }
          
          const rate = totalRecorded > 0 
            ? Math.round((totalDistributed / totalRecorded) * 100) 
            : 0;
          
          // Add week label
          const weekLabel = i === 0 
            ? 'This Week' 
            : i === 1 
              ? 'Last Week' 
              : `Week of ${formatWeekLabel(weekStart)}`;
          
          weeks.push(weekLabel);
          rates.push(rate);
          
        } catch (error) {
          console.error(`Error fetching data for week ${i}:`, error);
          weeks.push(`Week ${6-i}`);
          rates.push(0);
        }
      }
      
      setChartData({ weeks, rates });
      
    } catch (error) {
      console.error('Error fetching weekly distribution data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate min value for Y-axis (minimum value - 20, but not less than 0)
  const minRate = Math.min(...chartData.rates.filter(rate => rate > 0));
  const yAxisMin = Math.max(0, (isFinite(minRate) ? minRate - 20 : 0));

  // Chart configuration
  const chartOptions = {
    chart: {
      type: 'bar',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '60%',
        dataLabels: {
          position: 'top',
        },
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val + "%";
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    colors: ['#3378FF'],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        gradientToColors: ['#66a6ff'],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.8,
        stops: [0, 100]
      }
    },
    xaxis: {
      categories: chartData.weeks,
      position: 'bottom',
      labels: {
        style: {
          colors: '#9ca3af',
          fontSize: '11px',
        },
        rotate: -45,
        rotateAlways: false
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      },
      crosshairs: {
        show: false
      },
      tooltip: {
        enabled: false,
      }
    },
    yaxis: {
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: true,
        formatter: function (val) {
          return val + "%";
        },
        style: {
          colors: '#9ca3af',
          fontSize: '12px',
        }
      },
      title: {
        text: 'Distribution Rate (%)',
        style: {
          color: '#6b7280',
          fontSize: '12px',
          fontWeight: 600
        }
      },
      min: yAxisMin,
      max: 100
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
      padding: {
        left: 10,
        right: 10
      }
    },
    tooltip: {
      y: {
        formatter: function(val) {
          return val + "%"
        }
      },
      style: {
        fontSize: '12px',
      }
    }
  };

  const series = [{
    name: 'Avg Distribution Rate',
    data: chartData.rates
  }];

  return (
    <Card style={{ 
      borderRadius: '15px', 
      border: 'none', 
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      height: '100%',
      minHeight: '400px',
      background: '#fff'
    }}>
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="font-weight-bold mb-1" style={{ color: '#1a202c', fontSize: '18px' }}>
              Weekly Average Distribution Rate
            </h5>
            <p className="text-muted small mb-0" style={{ fontSize: '13px' }}>
              Last 6 weeks performance
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            {!isLoading && (
              <span className="badge bg-success d-flex align-items-center gap-1" style={{ padding: '6px 12px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white' }}></span>
                {chartData.rates[chartData.rates.length - 1]}% This Week
              </span>
            )}
          </div>
        </div>
        
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Chart
            options={chartOptions}
            series={series}
            type="bar"
            height="320"
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default WeeklyDistributionRate;